import Vue from 'vue';
import { Subscriber } from '@/interfaces/notifications';
import { Commit } from 'vuex';

interface SubscriberState {
	subscribers: {
		[id: number]: Subscriber;
	};
}

export default {
	namespaced: true,
	state: {
		subscribers: {},
	} as SubscriberState,
	getters: {
		getSubscribers: (state: SubscriberState): Subscriber[] => {
			return Object.values(state.subscribers);
		},

		getSubscriber:
			(state: SubscriberState) =>
			(id: number): Subscriber | undefined => {
				return state.subscribers[id];
			},
	},
	mutations: {
		SET_SUBSCRIPTIONS(state: SubscriberState, subscribers: Subscriber[]): void {
			const ret: { [id: number]: Subscriber } = {};
			state.subscribers = subscribers.reduce((obj, subscriber) => {
				obj[subscriber.id] = subscriber;
				return obj;
			}, ret);
		},

		ADD_SUBSCRIPTION(state: SubscriberState, subscriber: Subscriber): void {
			state.subscribers = {
				...state.subscribers,
				[subscriber.id]: subscriber,
			};
		},

		CHANGE_SUBSCRIPTION(state: SubscriberState, subscriber: Subscriber): void {
			const oldClient = state.subscribers[subscriber.id] || {};
			state.subscribers = {
				...state.subscribers,
				[subscriber.id]: { ...oldClient, ...subscriber },
			};
		},

		REMOVE_SUBSCRIPTION(state: SubscriberState, id: number): void {
			Vue.delete(state.subscribers, id);
		},

		CLEAN_SUBSCRIPTIONS(state: SubscriberState): void {
			state.subscribers = {};
		},
	},
	actions: {
		async fetchSubscribers({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(
				`${process.env.VUE_APP_API_URL}/notifications/subscribers`
			);
			if (response && response.status === 200) {
				commit('SET_SUBSCRIPTIONS', response.data.data);
			}
		},
		async addSubscriber(
			{ commit }: { commit: Commit },
			{ subscriber }: { subscriber: Partial<Subscriber> }
		): Promise<void> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/notifications/subscribers`,
				subscriber
			);
			if (response && response.status === 201) {
				commit('ADD_SUBSCRIPTION', response.data.data);
			}
		},
		async removeSubscriber({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/notifications/subscribers/${id}`
			);
			if (response && response.status === 200) {
				commit('REMOVE_SUBSCRIPTION', id);
			}
		},
		async modifySubscriber(
			{ commit }: { commit: Commit },
			{ id, subscriber }: { id: number; subscriber: Partial<Subscriber> }
		): Promise<void> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/notifications/subscribers/${id}`,
				subscriber
			);
			if (response && response.status === 200) {
				commit('CHANGE_SUBSCRIPTION', response.data.data);
			}
		},
		clean({ commit }: { commit: Commit }): void {
			commit('CLEAN_SUBSCRIPTIONS');
		},
		cleanAll({ commit }: { commit: Commit }): void {
			commit('CLEAN_SUBSCRIPTIONS');
		},
	},
};
